import { User, LogOut, MessageSquare } from "lucide-react";
import { router, usePage } from "@inertiajs/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AdminUserSwitcher } from "@/components/AdminUserSwitcher";
import { AgencyUserSwitcher } from "@/components/AgencyUserSwitcher";

function formatNumber(n: number): string {
  return n.toLocaleString("ja-JP");
}

function MessageQuotaBadge({ quota }: { quota: { usage: number | null; limit: number | null } }) {
  if (quota.usage === null) return null;

  const usage = quota.usage;
  const limit = quota.limit;
  const isOverLimit = limit !== null && usage > limit;

  return (
    <div
      className={`hidden items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium sm:flex ${
        isOverLimit
          ? "border-destructive/30 bg-destructive/10 text-destructive"
          : "border-border bg-muted/50 text-muted-foreground"
      }`}
    >
      <MessageSquare className="h-3.5 w-3.5" />
      <span>
        {formatNumber(usage)}
        <span className="mx-0.5 text-muted-foreground/60">／</span>
        {limit !== null ? formatNumber(limit) : "unlimited"}
      </span>
    </div>
  );
}

export function AppHeader() {
  const { props } = usePage();
  const currentUser = (props as any).user || {
    name: "ゲスト",
    email: "guest@example.com",
  };
  const messageQuota = (props as any).messageQuota;
  const isAdminSession = !!(props as any).currentAdmin;
  const isAgencySession = !!(props as any).currentAgency;

  const handleLogout = () => {
    router.delete('/users/sign_out');
  };

  return (
    <header className="flex h-16 items-center justify-between border-b border-border bg-admin-header px-4">
      <div className="flex items-center gap-4">
        <SidebarTrigger className="text-muted-foreground hover:text-foreground" />
      </div>

      <div className="flex items-center gap-4">
        {isAdminSession && <AdminUserSwitcher />}
        {!isAdminSession && isAgencySession && <AgencyUserSwitcher />}

        {messageQuota && <MessageQuotaBadge quota={messageQuota} />}

        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-3 rounded-full bg-transparent p-2 text-sm font-semibold text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
            <Avatar className="h-8 w-8">
              <AvatarFallback>
                <User className="h-4 w-4" />
              </AvatarFallback>
            </Avatar>

            <span className="hidden sm:inline-block">{currentUser.displayName || currentUser.name} 様</span>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              ログアウト
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
